import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAuthorizationContext } from "contexts/AuthorizationContext";
import { pickSingleOrganizationPageOrg, sameGuid } from "contexts/orgFilter";
import { getAllGateways, getAllSites } from "@redux/selectors";
import api from "services/backendService";
import utils from "utils";

import buildHierarchyContext from "./buildHierarchyContext";

async function safeGetOrganizationMembers(organizationGuid) {
  try {
    return await api.getGroupMembers(organizationGuid);
  } catch (error) {
    utils.debug(error, utils.MSG_TYPE_ERR);
    return [];
  }
}

function mergeDistinctUsers(usersByOrganization) {
  const mergedUsers = [];
  const seenIds = new Set();

  for (const users of usersByOrganization) {
    if (!Array.isArray(users)) continue;

    for (const candidate of users) {
      const id = candidate.userId ?? candidate.id;
      if (!id || seenIds.has(id)) continue;
      seenIds.add(id);
      mergedUsers.push(candidate);
    }
  }

  return { mergedUsers, seenIds };
}

function shouldResetOrganizationUsers(organizationGuids) {
  return !Array.isArray(organizationGuids) || organizationGuids.length === 0;
}

function filterUserIds(userIds, validIds) {
  return userIds.filter((userId) => validIds.has(userId));
}

/**
 * What the sites and devices selector has picked, for its summary: the sites,
 * gateways and controllers counted, or null when nothing at all is picked.
 */
export function describeHierarchySelection(t, selectedMap = {}) {
  const organizationCount = selectedMap.organization?.length || 0;
  const siteCount = selectedMap.site?.length || 0;
  const gatewayCount = selectedMap.gateway?.length || 0;
  const controllerCount = selectedMap.controller?.length || 0;

  if (
    organizationCount === 0 &&
    siteCount === 0 &&
    gatewayCount === 0 &&
    controllerCount === 0
  ) {
    return null;
  }

  const parts = [];

  if (siteCount > 0) {
    parts.push(t("auditLog.sitesSelected", { count: siteCount }));
  }

  if (gatewayCount > 0) {
    parts.push(t("auditLog.gatewaysSelected", { count: gatewayCount }));
  }

  if (controllerCount > 0) {
    parts.push(
      t("auditLog.controllersSelected", { count: controllerCount })
    );
  }

  return parts.join(" • ");
}

/**
 * What the audit log reads, for the export dialog and the Audit log page alike:
 * the organization, the sites and devices picked in it, and its users.
 *
 * `isActive` is false while the dialog is closed, so the selection is not set
 * up behind it; the page is always active.
 */
export default function useAuditLogScope({ isActive = true } = {}) {
  const { t } = useTranslation("common");
  const {
    activeOrganization,
    organizations,
    isSuperAdmin,
    groups,
    isOrgFilterEnabled,
    filteredOrgGuid,
    canViewAuditLogFor,
    filteredSite,
  } = useAuthorizationContext();

  // With the organization filter the audit log works on one organization, for
  // super admins too: the one chosen in the audit log, else the filter when the
  // audit log is usable there, else the only organization where it is;
  // otherwise the user chooses. The audit log shows it selected. With no
  // organization filter, the site filter's organization (AB#50180) stands in
  // for it, and the site itself is preselected.
  const usableOrganizations = useMemo(
    () => (isOrgFilterEnabled ? (organizations || []).filter((org) => canViewAuditLogFor(org.guid)) : []),
    // canViewAuditLogFor reads the organizations and the groups.
    [isOrgFilterEnabled, organizations, groups]
  );
  const userIsSuperAdmin = isSuperAdmin() && !isOrgFilterEnabled;
  // When the filter does not settle the organization, the one chosen in the
  // audit log is kept there, and the filter is left alone.
  const [chosenOrganizationGuid, setChosenOrganizationGuid] = useState(null);
  const chosenUsableOrganizationGuid = usableOrganizations.find(
    (org) => org.guid === chosenOrganizationGuid
  )?.guid;
  const activeOrganizationGuid = isOrgFilterEnabled
    ? chosenUsableOrganizationGuid ??
      pickSingleOrganizationPageOrg(usableOrganizations, filteredOrgGuid ?? filteredSite?.orgGuid ?? null)
    : activeOrganization?.guid;
  const mustChooseOrganization = isOrgFilterEnabled && !activeOrganizationGuid;

  const [selectedOrganizationGuids, setSelectedOrganizationGuids] = useState(
    []
  );
  const [selectedSiteGuids, setSelectedSiteGuids] = useState([]);
  const [selectedGatewayIdentifiers, setSelectedGatewayIdentifiers] = useState(
    []
  );
  const [selectedControllerGuids, setSelectedControllerGuids] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [isLoadingOrganizationUsers, setIsLoadingOrganizationUsers] =
    useState(false);

  useEffect(() => {
    if (userIsSuperAdmin) return;
    if (!activeOrganizationGuid) return;

    setSelectedOrganizationGuids((prev) => {
      if (prev.length === 1 && prev[0] === activeOrganizationGuid) {
        return prev;
      }

      return [activeOrganizationGuid];
    });
  }, [activeOrganizationGuid, userIsSuperAdmin]);

  useEffect(() => {
    if (!isActive) return;

    if (userIsSuperAdmin) {
      setSelectedOrganizationGuids([]);
      return;
    }

    if (!activeOrganizationGuid) return;
    setSelectedOrganizationGuids([activeOrganizationGuid]);
    // The site filter's site, when it is in that organization.
    if (filteredSite && sameGuid(filteredSite.orgGuid, activeOrganizationGuid)) {
      setSelectedSiteGuids([filteredSite.siteGuid]);
    }
  }, [isActive, userIsSuperAdmin, activeOrganizationGuid, filteredSite?.siteGuid]);

  const allSites = useSelector(getAllSites);
  const allGateways = useSelector(getAllGateways);

  const {
    organizationHierarchyData,
    organizationByGuid,
    siteByGuid,
    siteToOrganizationGuid,
    gatewayByIdentifier,
    controllerGuidToGatewayIdentifier,
  } = useMemo(
    () =>
      buildHierarchyContext({
        organizations: organizations || [],
        sites: allSites || [],
        gateways: allGateways || [],
        includeAllOrganizations: userIsSuperAdmin,
        activeOrganizationGuid,
      }),
    [organizations, allSites, allGateways, activeOrganizationGuid]
  );

  // Collect organization GUIDs from selected organizations, sites, gateways, or controllers
  //  - This is used to determine which organizations to load users for
  const selectedOrganizationGuidsForUsers = useMemo(() => {
    const groupGuids = new Set();

    for (const guid of selectedOrganizationGuids) {
      if (organizationByGuid.has(guid)) {
        groupGuids.add(guid);
      }
    }

    for (const siteGuid of selectedSiteGuids) {
      const orgGuid = siteToOrganizationGuid.get(siteGuid);
      if (orgGuid) {
        groupGuids.add(orgGuid);
      }
    }

    for (const gatewayIdentifier of selectedGatewayIdentifiers) {
      const gateway = gatewayByIdentifier.get(gatewayIdentifier);
      const orgGuid = gateway?.customer?.customerGuid;
      if (orgGuid) {
        groupGuids.add(orgGuid);
      }
    }

    for (const controllerGuid of selectedControllerGuids) {
      const gatewayIdentifier =
        controllerGuidToGatewayIdentifier.get(controllerGuid);
      const gateway = gatewayByIdentifier.get(gatewayIdentifier);
      const orgGuid = gateway?.customer?.customerGuid;
      if (orgGuid) {
        groupGuids.add(orgGuid);
      }
    }

    return Array.from(groupGuids).filter((orgGuid) => organizationByGuid.get(orgGuid)?.id);
  }, [
    selectedOrganizationGuids,
    selectedSiteGuids,
    selectedGatewayIdentifiers,
    selectedControllerGuids,
  ]);

  // Another organization: what was picked in the previous one does not apply.
  const chooseOrganization = (orgGuid) => {
    if (orgGuid === activeOrganizationGuid) return;
    setChosenOrganizationGuid(orgGuid);
    setSelectedSiteGuids([]);
    setSelectedGatewayIdentifiers([]);
    setSelectedControllerGuids([]);
    setSelectedUserIds([]);
  };

  // Back to nothing chosen and nothing picked, as when the dialog first opens.
  const reset = () => {
    setChosenOrganizationGuid(null);
    setSelectedOrganizationGuids([]);
    setSelectedSiteGuids([]);
    setSelectedGatewayIdentifiers([]);
    setSelectedControllerGuids([]);
    setSelectedUserIds([]);
    setOrganizationUsers([]);
    setIsLoadingOrganizationUsers(false);
  };

  useEffect(() => {
    let isCancelled = false;

    if (shouldResetOrganizationUsers(selectedOrganizationGuidsForUsers)) {
      setOrganizationUsers([]);
      setSelectedUserIds([]);
      setIsLoadingOrganizationUsers(false);
      return () => {
        isCancelled = true;
      };
    }

    setIsLoadingOrganizationUsers(true);
    setOrganizationUsers([]);

    Promise.all(selectedOrganizationGuidsForUsers.map(safeGetOrganizationMembers))
      .then((results) => {
        if (isCancelled) return;

        const { mergedUsers, seenIds } = mergeDistinctUsers(results);
        setOrganizationUsers(mergedUsers);
        setSelectedUserIds((prev) => filterUserIds(prev, seenIds));
      })
      .catch((error) => {
        if (!isCancelled) {
          utils.debug(error, utils.MSG_TYPE_ERR);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingOrganizationUsers(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedOrganizationGuidsForUsers]);

  const selectedOrganization = useMemo(() => {
    const getOrgByGuid = (orgGuid) =>
      orgGuid ? (organizationByGuid.get(orgGuid) ?? null) : null;
    const orgFromOrgGuids = () =>
      selectedOrganizationGuids.map((g) => getOrgByGuid(g)).find(Boolean) ??
      null;

    const orgFromSites = () =>
      selectedSiteGuids
        .map((siteGuid) => getOrgByGuid(siteToOrganizationGuid.get(siteGuid)))
        .find(Boolean) ?? null;

    const orgFromGateways = () =>
      selectedGatewayIdentifiers
        .map((id) =>
          getOrgByGuid(gatewayByIdentifier.get(id)?.customer?.customerGuid)
        )
        .find(Boolean) ?? null;

    const orgFromControllers = () =>
      selectedControllerGuids
        .map((controllerGuid) => {
          const gwId = controllerGuidToGatewayIdentifier.get(controllerGuid);
          return getOrgByGuid(
            gatewayByIdentifier.get(gwId)?.customer?.customerGuid
          );
        })
        .find(Boolean) ?? null;

    return (
      orgFromOrgGuids() ??
      orgFromSites() ??
      orgFromGateways() ??
      orgFromControllers() ??
      null
    );
  }, [
    selectedOrganizationGuids,
    selectedSiteGuids,
    selectedGatewayIdentifiers,
    selectedControllerGuids,
  ]);

  const selectedOrganizationGuid = selectedOrganization?.guid;

  const handleHierarchySelectionChange = useCallback(
    (nextSelectedIdsByType = {}) => {
      const nextOrganizations = Array.isArray(
        nextSelectedIdsByType.organization
      )
        ? nextSelectedIdsByType.organization
        : [];
      const nextSites = Array.isArray(nextSelectedIdsByType.site)
        ? nextSelectedIdsByType.site
        : [];
      const nextGateways = Array.isArray(nextSelectedIdsByType.gateway)
        ? nextSelectedIdsByType.gateway
        : [];
      const nextControllers = Array.isArray(nextSelectedIdsByType.controller)
        ? nextSelectedIdsByType.controller
        : [];

      const getOrgFromGateway = (gatewayIdentifier) => {
        const gateway = gatewayByIdentifier.get(gatewayIdentifier);
        return gateway?.customer?.customerGuid || null;
      };

      const getOrgFromController = (controllerGuid) => {
        const gatewayIdentifier =
          controllerGuidToGatewayIdentifier.get(controllerGuid);
        if (!gatewayIdentifier) return null;
        return getOrgFromGateway(gatewayIdentifier);
      };

      const lockedOrgGuid = userIsSuperAdmin ? null : activeOrganizationGuid;

      if (lockedOrgGuid) {
        const filteredSites = nextSites.filter(
          (siteGuid) => siteToOrganizationGuid.get(siteGuid) === lockedOrgGuid
        );

        const filteredGateways = nextGateways.filter(
          (gatewayIdentifier) =>
            getOrgFromGateway(gatewayIdentifier) === lockedOrgGuid
        );

        const filteredControllers = nextControllers.filter(
          (controllerGuid) =>
            getOrgFromController(controllerGuid) === lockedOrgGuid
        );

        setSelectedOrganizationGuids([lockedOrgGuid]);
        setSelectedSiteGuids(filteredSites);
        setSelectedGatewayIdentifiers(filteredGateways);
        setSelectedControllerGuids(filteredControllers);
        return;
      }

      const selectionContainsOrg = (orgGuid) => {
        return (
          !!orgGuid &&
          (nextOrganizations.includes(orgGuid) ||
            nextSites.some(
              (siteGuid) => siteToOrganizationGuid.get(siteGuid) === orgGuid
            ) ||
            nextGateways.some(
              (gatewayIdentifier) =>
                getOrgFromGateway(gatewayIdentifier) === orgGuid
            ) ||
            nextControllers.some(
              (controllerGuid) =>
                getOrgFromController(controllerGuid) === orgGuid
            ))
        );
      };

      const findCurrentOrgGuid = () => {
        for (const guid of selectedOrganizationGuids) {
          if (guid) return guid;
        }
        for (const siteGuid of selectedSiteGuids) {
          const orgGuid = siteToOrganizationGuid.get(siteGuid);
          if (orgGuid) return orgGuid;
        }
        for (const gatewayIdentifier of selectedGatewayIdentifiers) {
          const orgGuid = getOrgFromGateway(gatewayIdentifier);
          if (orgGuid) return orgGuid;
        }
        for (const controllerGuid of selectedControllerGuids) {
          const orgGuid = getOrgFromController(controllerGuid);
          if (orgGuid) return orgGuid;
        }
        return null;
      };

      const findNextOrgGuid = () => {
        for (const guid of nextOrganizations) {
          if (guid) return guid;
        }
        for (const siteGuid of nextSites) {
          const orgGuid = siteToOrganizationGuid.get(siteGuid);
          if (orgGuid) return orgGuid;
        }
        for (const gatewayIdentifier of nextGateways) {
          const orgGuid = getOrgFromGateway(gatewayIdentifier);
          if (orgGuid) return orgGuid;
        }
        for (const controllerGuid of nextControllers) {
          const orgGuid = getOrgFromController(controllerGuid);
          if (orgGuid) return orgGuid;
        }
        return null;
      };

      let targetOrgGuid = findCurrentOrgGuid();

      if (targetOrgGuid && !selectionContainsOrg(targetOrgGuid)) {
        targetOrgGuid = null;
      }

      if (!targetOrgGuid) {
        targetOrgGuid = findNextOrgGuid();
      }

      if (!targetOrgGuid) {
        setSelectedOrganizationGuids([]);
        setSelectedSiteGuids([]);
        setSelectedGatewayIdentifiers([]);
        setSelectedControllerGuids([]);
        return;
      }

      const nextOrganizationGuids = nextOrganizations.includes(targetOrgGuid)
        ? [targetOrgGuid]
        : [];
      const nextSiteGuids = nextSites.filter(
        (siteGuid) => siteToOrganizationGuid.get(siteGuid) === targetOrgGuid
      );
      const nextGatewayIdentifiers = nextGateways.filter(
        (gatewayIdentifier) =>
          getOrgFromGateway(gatewayIdentifier) === targetOrgGuid
      );
      const nextControllerGuids = nextControllers.filter(
        (controllerGuid) =>
          getOrgFromController(controllerGuid) === targetOrgGuid
      );

      setSelectedOrganizationGuids(nextOrganizationGuids);
      setSelectedSiteGuids(nextSiteGuids);
      setSelectedGatewayIdentifiers(nextGatewayIdentifiers);
      setSelectedControllerGuids(nextControllerGuids);
    },
    [
      controllerGuidToGatewayIdentifier,
      gatewayByIdentifier,
      selectedControllerGuids,
      selectedGatewayIdentifiers,
      selectedOrganizationGuids,
      selectedSiteGuids,
      siteToOrganizationGuid,
      activeOrganizationGuid,
      userIsSuperAdmin,
    ]
  );

  const organizationSelectionLocked =
    Boolean(activeOrganizationGuid) && !userIsSuperAdmin;

  const selectedIdsByType = useMemo(
    () => ({
      organization: organizationSelectionLocked
        ? []
        : selectedOrganizationGuids,
      site: selectedSiteGuids,
      gateway: selectedGatewayIdentifiers,
      controller: selectedControllerGuids,
    }),
    [
      organizationSelectionLocked,
      selectedOrganizationGuids,
      selectedSiteGuids,
      selectedGatewayIdentifiers,
      selectedControllerGuids,
    ]
  );

  const hierarchyLabels = useMemo(
    () => ({
      placeholder: t("auditLog.placeholder"),
      searchPlaceholder: t("auditLog.search"),
      noResults: t("auditLog.noResult"),
    }),
    [t]
  );

  const visibleHierarchyData = useMemo(() => {
    if (organizationSelectionLocked) {
      const orgGuid = activeOrganizationGuid || selectedOrganizationGuid;
      if (!orgGuid) {
        return [];
      }

      const orgNode = organizationHierarchyData.find(
        (node) => node?.id === orgGuid
      );
      return orgNode?.items || [];
    }

    if (!selectedOrganizationGuid) {
      return organizationHierarchyData;
    }

    const filtered = organizationHierarchyData.filter(
      (orgNode) => orgNode?.id === selectedOrganizationGuid
    );
    if (filtered.length === 0) {
      return organizationHierarchyData;
    }

    return filtered;
  }, [
    organizationHierarchyData,
    selectedOrganizationGuid,
    organizationSelectionLocked,
    activeOrganizationGuid,
  ]);

  return {
    usableOrganizations,
    activeOrganizationGuid,
    mustChooseOrganization,
    chooseOrganization,
    reset,
    selectedSiteGuids,
    selectedGatewayIdentifiers,
    selectedControllerGuids,
    selectedUserIds,
    setSelectedUserIds,
    organizationUsers,
    isLoadingOrganizationUsers,
    siteByGuid,
    gatewayByIdentifier,
    controllerGuidToGatewayIdentifier,
    selectedOrganization,
    selectedOrganizationGuid,
    organizationSelectionLocked,
    handleHierarchySelectionChange,
    selectedIdsByType,
    hierarchyLabels,
    visibleHierarchyData,
  };
}
